import {
  Observable,
  debounceTime,
  filter,
  fromEvent,
  map,
  merge,
  mergeMap,
  of,
  shareReplay,
  startWith,
  tap,
} from "rxjs";
import { Failure } from "src/js/helpers/failure.js";
import { uniqBy } from "lodash-es";
import { UGElement } from "src/js/models/module.js";
import { array, either, nonEmptyArray, option, record } from "fp-ts";
import { Option } from "fp-ts/lib/Option";
import { constTrue, flow, pipe, tuple } from "fp-ts/lib/function";
import { values as recordValues } from "fp-ts-std/Record";
import { FilterParser } from "../parsers/moduleFilterParser";
import { Filter, FilterDefinition } from "../parsers/moduleFilterParser";
import * as Log from "../../helpers/browserConsole";
import { ModuleFilter } from "./moduleFilter";
import Choices from "choices.js";

const inputType = ["choices", "checkbox", "radio", "text"] as const;
type InputType = (typeof inputType)[number];

type InputWithChoices = HTMLInputElement & { choices: Choices };

export type FilterPanelParameters = {
  containerElement: HTMLElement;
  initialState: FilterState;
  defaultFilter: (m: UGElement) => Boolean;
};

type FilterPanelSetStateEvent = {
  type: "SET_STATE";
  state: FilterState;
};
type FilterPanelSetUrlEvent = {
  type: "SET_URL";
  state: FilterState;
};
type FilterPanelStateEvent = FilterPanelSetStateEvent | FilterPanelSetUrlEvent;

type UrlAndFilterDefinition = [string, FilterDefinition];
type UrlAndFilter = [string, Filter];
export type FilterDefinitionByUrl = Record<string, FilterDefinition>;
type FilterByUrl = Record<string, Filter>;

type FilterElement = {
  element: InputWithChoices;
  elementType: InputType;
  url: string;
  filterDefinition: FilterDefinition;
};

export type FilterState = FilterByUrl;

export class FilterPanel {
  static EncodeFilterState(filterByUrl: FilterState): Record<string, string[]> {
    return pipe(
      filterByUrl,
      record.map((filter) => filter.values)
    );
  }
  static DecodeFilterState(
    filterDefinitionsByUrl: Record<string, FilterDefinition>,
    encodedState: Record<string, string[]>
  ): FilterState {
    return pipe(
      encodedState,
      record.filterMapWithIndex((url, vals) =>
        pipe(
          vals,
          nonEmptyArray.fromArray,
          option.map(
            flow(
              (vals) => option.of({ values: vals }),
              option.bind("definition", () =>
                record.lookup(url)(filterDefinitionsByUrl)
              ),
              option.map(({ values, definition }) => ({
                values,
                definition: definition,
              })),
              option.getOrElseW(() => Failure(`Missing filter url "${url}"`))
            )
          )
        )
      )
    );
  }

  private static ExtractFilterElementConfig(
    element: HTMLElement
  ): Option<UrlAndFilterDefinition> {
    const MaybeUrl = option.fromNullable(element.dataset.filterUrl);
    const MaybeFilterDefinitionString = option.fromNullable(
      element.dataset.filter
    );

    // parse filterDefinition or fail
    const MaybeFilterDefinition = option.map(
      flow(FilterParser.parseFilterDefinition, either.getOrElseW(Failure))
    )(MaybeFilterDefinitionString);

    return pipe(
      option.Do,
      option.bind("url", () => MaybeUrl),
      option.bind("filterDefinition", () => MaybeFilterDefinition),
      option.map(({ url, filterDefinition }) => tuple(url, filterDefinition))
    );
  }

  static ExtractFilterConfig(container: Element): FilterDefinitionByUrl {
    return pipe(
      container.querySelectorAll<HTMLElement>("[data-filter]"),
      Array.from<HTMLElement>,
      array.map(FilterPanel.ExtractFilterElementConfig),
      array.compact,
      (list) => uniqBy(list, ([url, _]) => url),
      record.fromEntries
    );
  }

  static MakeFilterFunction<T extends object>(
    state: FilterState
  ): (x: T[]) => T[] {
    const predicates: ((x: T) => boolean)[] = pipe(
      state,
      recordValues,
      array.map(FilterPanel.#filterToPredicate)
    );

    return (list: T[]) =>
      predicates.reduce(
        (remainingList, predicate) => remainingList.filter(predicate),
        list
      );
  }

  private static MakeFilterPanelStateEvent(
    eventType: string,
    filterElement: Element
  ): FilterPanelStateEvent {
    const filterElementType =
      filterElement.attributes.getNamedItem("type")?.value ?? "UNKNOWN";
    if (eventType == "input" && filterElementType == "text") {
      // if input event on text element, then only set state, not url
      return { type: "SET_STATE", state: record.fromEntries([]) };
    }
    return { type: "SET_URL", state: record.fromEntries([]) };
  }

  private static MakeSetStateEvent(
    state: FilterState
  ): FilterPanelSetStateEvent {
    return { type: "SET_STATE", state };
  }
  private static MakeSetUrlEvent(state: FilterState): FilterPanelSetUrlEvent {
    return { type: "SET_URL", state };
  }
  private static SetEventState(
    event: FilterPanelStateEvent,
    state: FilterState
  ): FilterPanelStateEvent {
    event.type;
    return { type: event.type, state: state };
  }

  static Configure(
    config: FilterPanelParameters
  ): Observable<FilterPanelStateEvent> {
    let state = config.initialState;
    pipe(
      state,
      record.toEntries,
      array.map((urlAndFilter) =>
        FilterPanel.#setInputElementsFromUrlAndFilter(
          config.containerElement,
          urlAndFilter
        )
      )
    );

    return merge(
      pipe(fromEvent(config.containerElement, "input"), debounceTime(1000)),
      fromEvent(config.containerElement, "change"),
      fromEvent(config.containerElement, "focusout")
    ).pipe(
      map((event) =>
        pipe(
          event,
          (e) => e.target as Element | null,
          option.fromNullable,
          option.flatMap((target) =>
            option.fromNullable(target.closest<HTMLElement>("[data-filter]"))
          ),
          option.map((x) => ({
            event: FilterPanel.MakeFilterPanelStateEvent(event.type, x),
            filterElement: x,
          }))
        )
      ),
      filter(option.isSome),
      map(option.getOrElseW(() => Failure("Should never be able to happen"))),
      map(({ event, filterElement }) => ({
        event,
        urlAndFilter: FilterPanel.#getUrlAndFilterFromElement(filterElement),
      })),
      tap(({ urlAndFilter }) =>
        FilterPanel.#setInputElementsFromUrlAndFilter(
          config.containerElement,
          urlAndFilter
        )
      ), // update other dom elements that modify filter on the same property
      map(({ event, urlAndFilter }) =>
        FilterPanel.SetEventState(
          event,
          FilterPanel.#updateState(state, urlAndFilter)
        )
      ),

      tap((event) => (state = event.state)),
      // if SET_URL event. emit both a set-state and a set-url event
      mergeMap((event) => {
        return event.type == "SET_STATE"
          ? of(event)
          : of(event, FilterPanel.MakeSetStateEvent(event.state));
      }),
      startWith(FilterPanel.MakeSetStateEvent(config.initialState))
    ) as Observable<FilterPanelStateEvent>;
  }

  static #updateState(
    filterState: FilterState,
    filterAndUrl: UrlAndFilter
  ): FilterState {
    const newFilter = { ...filterState };
    newFilter[filterAndUrl[0]] = filterAndUrl[1];
    return newFilter;
  }

  static #getUrlAndFilterFromElement(element: HTMLElement): UrlAndFilter {
    const filterElement = FilterPanel.#toFilterElement(
      element as InputWithChoices
    );
    const filter = FilterPanel.#getFilterValueFromFilterElement(filterElement);
    return tuple(filterElement.url, filter);
  }

  static #FilterFromElementAndValue(
    element: FilterElement,
    values: string[]
  ): Filter {
    return {
      definition: element.filterDefinition,
      values: values,
    };
  }

  // Given a Filter value, return
  //   a function that, given an object
  //   returns true if the object matches the filter
  static #filterToPredicate<T extends object>(
    filter: Filter
  ): (t: T) => boolean {
    if (filter.values.length == 0) {
      return constTrue;
    } else return ModuleFilter.asPredicate(filter);
  }

  static #toFilterElement(element: InputWithChoices): FilterElement {
    const checkBoxes = Array.from(
      element.querySelectorAll<HTMLInputElement>('[type="checkbox"]')
    );
    const choicesInstance: Option<Choices> = option.fromNullable(
      element.choices
    );

    const inputType: InputType = option.isSome(choicesInstance)
      ? "choices"
      : element?.type.toLowerCase() == "radio"
      ? "radio"
      : checkBoxes.length > 0
      ? "checkbox"
      : "text";

    return pipe(
      FilterPanel.ExtractFilterElementConfig(element),
      option.getOrElseW(() =>
        Failure(`"${element}": is in invalid filter element`)
      ),
      ([url, filterDefinition]) => ({
        element: element,
        elementType: inputType,
        filterDefinition: filterDefinition,
        url: url,
      })
    );
  }

  static #getFilterValueFromFilterElement(element: FilterElement): Filter {
    const asFilter = (values: string[]) =>
      FilterPanel.#FilterFromElementAndValue(element, values);
    const ensureArray: (vals: any | any[]) => any[] = (values) =>
      [values].flat();

    const wrapValue = flow(
      ensureArray,
      array.filter((x) => !!x),
      asFilter
    );

    switch (element.elementType) {
      case "choices":
        return pipe(
          element.element,
          (elm) => elm?.choices?.getValue(true),
          wrapValue
        );
      case "radio":
        return pipe(element.element.value, wrapValue);
      case "checkbox":
        return pipe(
          element.element,
          (elm) =>
            Array.from(
              elm.querySelectorAll<HTMLInputElement>("[type='checkbox']")
            ),
          (boxes: HTMLInputElement[]) =>
            boxes
              .filter((checkbox) => checkbox.checked)
              .map((checkbox) => checkbox.value),
          wrapValue
        );
      case "text":
        return pipe(
          element.element as HTMLInputElement,
          (elm) => elm.value,
          wrapValue
        );
      default:
        Failure(`"unexpected input type "${element.elementType}"`);
    }
  }

  static #setInputElementsFromUrlAndFilter(
    container: Element,
    urlAndFilter: UrlAndFilter
  ) {
    const url = urlAndFilter[0];
    const filter = urlAndFilter[1];

    Array.from(
      container.querySelectorAll<InputWithChoices>(`[data-filter-url="${url}"]`)
    )
      .map(FilterPanel.#toFilterElement)
      .map((filterElement) =>
        FilterPanel.#setInputElementFromFilterValue(filterElement, filter)
      );
  }

  static #setInputElementFromFilterValue(
    filterElement: FilterElement,
    filterValue: Filter
  ) {
    switch (filterElement.elementType) {
      case "choices":
        filterElement.element.choices.setChoiceByValue(filterValue.values);
        break;
      case "radio":
        if (filterValue.values.length > 1) {
          Failure("Only none or 1 value is accepted");
        }
        if (filterElement.element.value == filterValue.values[0])
          filterElement.element.checked = true;
        break;
      case "checkbox":
        Array.from(
          filterElement.element.querySelectorAll<HTMLInputElement>(
            '[type="checkbox"]'
          )
        ).map((box) => (box.checked = filterValue.values.includes(box.value)));
        break;
      case "text":
        (filterElement.element as HTMLInputElement).value =
          filterValue.values[0] || "";
        break;
      default:
        Log.error(`unsuported filterType: ${filterElement.elementType}`);
        break;
    }
  }
}
